import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAiModels, useTranscribeStatus } from "@/lib/queries";
import { useUpdateAppSettings } from "@/lib/mutations";
import { formatPricing } from "@/features/settings/modelPricing";
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";

// Right-click model switcher for the floating dictation button (2026-10-11).
// Rendered inside the button's Popover — mounted ONLY while the menu is open,
// so useAiModels/useTranscribeStatus (both staleTime 0) fetch fresh data on
// every open and tear down on close. Picking a model PATCHes /settings
// { aiTranscribeModel } (requires settings.view + settings.ai — the button
// only opens this menu for admins/users holding both) and then invalidates
// ["ai-transcribe-status"] so the shared health probe re-runs against the NEW
// saved model. The button's tick/X badge and this menu's header both observe
// that query, so they always agree.
//
// The list mirrors the Settings ModelPicker item layout (name + pricing +
// slug, no truncation of names) but is list-only: custom slugs stay in
// Settings → AI.
export function DictationModelMenu() {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  // Optimistic display of the just-picked model until the probe echoes it.
  const [pendingModel, setPendingModel] = useState<string | null>(null);

  const { data: status, isFetching: statusFetching, error: statusError } = useTranscribeStatus();
  const { data: modelsData, isLoading: modelsLoading } = useAiModels();
  const update = useUpdateAppSettings();

  const models = modelsData?.transcription ?? [];

  // The probe response carries the SAVED model slug — once it echoes the
  // picked one, the optimistic override is confirmed and can be dropped.
  useEffect(() => {
    if (pendingModel && status?.model === pendingModel) setPendingModel(null);
  }, [pendingModel, status?.model]);

  const displayModel = pendingModel ?? status?.model ?? "";
  // "Checking…" while the first probe runs, a re-probe is in flight, or the
  // just-picked model hasn't been confirmed by the probe yet.
  const checking = statusFetching || (pendingModel !== null && status?.model !== pendingModel);

  const pick = async (id: string) => {
    setPendingModel(id);
    try {
      // Re-picking the already-saved model skips the save — it's a manual
      // re-check of availability (the invalidate below re-probes).
      if (id !== status?.model) {
        await update.mutateAsync({ aiTranscribeModel: id });
      }
    } catch {
      setPendingModel(null); // save failed — drop the optimistic display
      return; // the mutation's onError already toasted
    }
    // Re-probe the saved model; the shared query updates badge + header.
    await qc.invalidateQueries({ queryKey: ["ai-transcribe-status"] });
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? models.filter(
        (m) => m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q),
      )
    : models;

  return (
    <div className="flex max-h-[420px] flex-col">
      {/* Header: current model + live availability verdict */}
      <div className="border-b border-cream-200 px-3 py-2 dark:border-dark-700">
        <div className="text-xs font-medium text-muted">Transcription model</div>
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium">{displayModel || "—"}</span>
          {checking ? (
            <span className="flex shrink-0 items-center gap-1 text-xs text-amber-600">
              <Loader2 size={12} className="animate-spin" /> Checking…
            </span>
          ) : statusError ? (
            <span className="shrink-0 text-xs text-amber-600">Status unknown</span>
          ) : status?.ok ? (
            <span className="flex shrink-0 items-center gap-1 text-xs text-green-600">
              <Check size={12} /> Available
            </span>
          ) : (
            <div className="min-w-0">
              <span className="flex items-center gap-1 text-xs text-red-600">
                <X size={12} className="shrink-0" /> Unavailable
              </span>
              {status?.message && (
                <div className="truncate text-xs text-muted" title={status.message}>
                  {status.message}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Command shouldFilter={false} className="rounded-none">
        <CommandInput
          placeholder="Search models…"
          value={query}
          onValueChange={setQuery}
        />
        <CommandList className="visible-scrollbar">
          {modelsLoading && (
            <div className="py-6 text-center text-sm text-muted">Loading models…</div>
          )}
          {!modelsLoading && filtered.length === 0 && (
            <div className="p-3 text-center text-xs text-muted">
              {models.length === 0 ? (modelsData?.message ?? "No models available") : "No models match"}
            </div>
          )}
          {!modelsLoading && (
            <CommandGroup>
              {filtered.map((m) => (
                <CommandItem key={m.id} value={m.id} onSelect={() => void pick(m.id)}>
                  <Check className={cn("mr-1 h-4 w-4 shrink-0", displayModel === m.id ? "opacity-100" : "opacity-0")} />
                  {/* Stacked layout, no name truncation — mirrors ModelPicker. */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium">{m.name}</span>
                      <span className="shrink-0 whitespace-nowrap text-xs text-muted">
                        {formatPricing(m.pricing, true)}
                      </span>
                    </div>
                    <div className="text-xs text-muted">{m.id}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </div>
  );
}
